import { useEffect, useState } from "react";

/**
 * GiftAnimation — TikTok-style overlay rendered when a gift sends.
 *
 * Three tiers:
 *   small  → emoji floats up the right side and fades.
 *   medium → centered emoji with glow burst + light particles.
 *   large  → fullscreen overlay with massive emoji + backdrop dim + sparkles.
 *
 * Important: this component is rendered as a sibling of the AudioPlayer (NOT
 * a parent), and its overlay is `pointer-events-none` so it never absorbs
 * clicks intended for play/pause. Audio playback is never disturbed.
 */
const SMALL_DURATION = 2200;
const MEDIUM_DURATION = 2800;
const LARGE_DURATION = 3500;

const GiftAnimation = ({ gift, tier, onDone }) => {
  const [phase, setPhase] = useState("enter"); // enter -> hold -> exit
  useEffect(() => {
    const dur =
      tier === "large" ? LARGE_DURATION
      : tier === "medium" ? MEDIUM_DURATION
      : SMALL_DURATION;
    const t1 = setTimeout(() => setPhase("hold"), 200);
    const t2 = setTimeout(() => setPhase("exit"), dur - 400);
    const t3 = setTimeout(() => onDone && onDone(), dur);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [tier, onDone]);

  if (tier === "large") {
    return (
      <div
        className={`fixed inset-0 z-[100] flex items-center justify-center pointer-events-none transition-opacity duration-500 ${
          phase === "exit" ? "opacity-0" : "opacity-100"
        }`}
        data-testid="gift-animation-large"
      >
        {/* Dim backdrop */}
        <div className="absolute inset-0 bg-black/60" />
        {/* Burst rays */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-[120vw] h-[120vw] sm:w-[60vw] sm:h-[60vw] rounded-full opacity-60 bg-[radial-gradient(circle,_rgba(244,114,182,0.55)_0%,_rgba(168,85,247,0.4)_30%,_transparent_70%)] animate-ping" />
        </div>
        {/* Sparkle ring */}
        {[...Array(14)].map((_, i) => (
          <span
            key={i}
            className="absolute text-2xl select-none"
            style={{
              top: "50%",
              left: "50%",
              transform: `rotate(${(360 / 14) * i}deg) translate(0, -36vh)`,
              animation: `gift-sparkle 1.4s ${i * 0.05}s ease-out infinite`,
            }}
          >
            ✨
          </span>
        ))}
        {/* Main emoji */}
        <div
          className={`relative text-[28vh] sm:text-[36vh] select-none drop-shadow-[0_0_60px_rgba(244,114,182,0.8)] transform transition-all duration-700 ${
            phase === "enter" ? "scale-50 opacity-0 rotate-[-12deg]"
              : phase === "hold" ? "scale-110 opacity-100 rotate-0"
              : "scale-150 opacity-0 rotate-12"
          }`}
        >
          {gift.emoji}
        </div>
        {/* Name label */}
        <div className="absolute bottom-[14vh] left-0 right-0 text-center">
          <div className="inline-block px-5 py-2 rounded-full bg-black/60 border border-pink-500/40 text-white text-base sm:text-lg font-black tracking-wide">
            {gift.emoji} {gift.name} sent!
          </div>
        </div>
        <style>{`
          @keyframes gift-sparkle {
            0% { opacity: 0; transform: rotate(var(--r,0deg)) translate(0,-36vh) scale(0.5); }
            50% { opacity: 1; transform: rotate(var(--r,0deg)) translate(0,-32vh) scale(1.1); }
            100% { opacity: 0; transform: rotate(var(--r,0deg)) translate(0,-28vh) scale(0.7); }
          }
        `}</style>
      </div>
    );
  }

  if (tier === "medium") {
    return (
      <div
        className={`fixed bottom-24 left-1/2 -translate-x-1/2 z-[100] pointer-events-none transition-opacity duration-500 ${
          phase === "exit" ? "opacity-0" : "opacity-100"
        }`}
        data-testid="gift-animation-medium"
      >
        <div className="relative">
          <div className="absolute inset-0 -m-12 rounded-full bg-pink-500/30 blur-2xl animate-ping" />
          <div className={`relative text-7xl sm:text-8xl select-none transform transition-all duration-500 ${
            phase === "enter" ? "scale-50 opacity-0"
              : phase === "hold" ? "scale-110 opacity-100"
              : "scale-150 opacity-0"
          }`}>
            {gift.emoji}
          </div>
          <div className="mt-2 text-center">
            <span className="px-3 py-1 rounded-full bg-black/60 text-pink-200 text-xs font-bold border border-pink-500/40">
              +{gift.coins} coins
            </span>
          </div>
        </div>
      </div>
    );
  }

  // small — floats up the right edge
  return (
    <div
      className={`fixed bottom-32 right-6 sm:right-12 z-[100] pointer-events-none transition-opacity duration-500 ${
        phase === "exit" ? "opacity-0" : "opacity-100"
      }`}
      data-testid="gift-animation-small"
    >
      <div className={`text-5xl sm:text-6xl select-none transform transition-all ease-out ${
        phase === "enter" ? "translate-y-8 opacity-0"
          : phase === "hold" ? "-translate-y-2 opacity-100"
          : "-translate-y-24 opacity-0"
      }`} style={{ transitionDuration: "1800ms" }}>
        {gift.emoji}
      </div>
    </div>
  );
};

export default GiftAnimation;
